import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useMsalAuth } from "../api/msal";
import { useLoginMicrosoftMutation } from "../api";
import {
  LoginCard,
  LoginFooter,
} from "../components/authentication/auth.exports";
import {
  useAuthState,
  useAuthComplete,
  useAuthError,
  useMessage,
} from "../hooks";
import { handleMsalRedirectResponse } from "../handlers/authHandlers";
import { useAppSelector } from "../store";

// Check for redirect params immediately, outside component to avoid re-calculation
const checkForRedirectParams = () => {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash;
  const search = window.location.search;
  return (
    hash.includes("code=") ||
    hash.includes("id_token=") ||
    hash.includes("access_token=") ||
    search.includes("code=") ||
    search.includes("state=")
  );
};

export function LoginPage() {
  const { instance, accounts, inProgress } = useMsal();
  const { isLoading, setIsLoading } = useAuthState();
  const navigate = useNavigate();
  const [loginMicrosoft] = useLoginMicrosoftMutation();
  const authComplete = useAuthComplete();
  const handleAuthError = useAuthError();
  const message = useMessage();
  const hasHandled = useRef(false);
  const msal = useMsalAuth();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const suspended =
    (location.state as { suspended?: boolean })?.suspended ?? false;

  const [isProcessingRedirect, setIsProcessingRedirect] = useState(
    () => checkForRedirectParams() || accounts.length > 0,
  );

  // Redirect if already authenticated - prevent infinite loop
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hasNavigated.current) {
      hasNavigated.current = true;
      try {
        navigate("/customers", { replace: true });
      } catch (error) {
        console.error("Navigation failed:", error);
        hasNavigated.current = false;
      }
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (inProgress !== "none" || hasHandled.current) return;

    hasHandled.current = true;

    // Add 30-second timeout for MSAL redirect handling
    const timeout = setTimeout(() => {
      setIsProcessingRedirect(false);
      message.error("Authentication timeout. Please try again.");
      hasHandled.current = false;
    }, 30000); // 30 second timeout

    handleMsalRedirectResponse({
      instance,
      accounts,
      loginMicrosoft,
      authComplete,
      handleAuthError,
      navigate,
      setIsProcessingRedirect,
      message,
    }).finally(() => {
      clearTimeout(timeout);
    });

    return () => clearTimeout(timeout);
  }, [
    instance,
    accounts,
    inProgress,
    loginMicrosoft,
    authComplete,
    handleAuthError,
    navigate,
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8 relative">
        <img
          className="absolute top-[66%] hidden md:block w-[80%] "
          src="service_x_black.svg"
          alt="Background decoration"
        />
        <LoginCard
          msal={msal}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isProcessingRedirect={inProgress !== "none" || isProcessingRedirect}
          authComplete={authComplete}
          suspended={suspended}
          loginMicrosoft={loginMicrosoft}
        />
      </div>
      <LoginFooter />
    </div>
  );
}

export default LoginPage;
